const stats = [
  { stat: "10+ Years", label: "Application Security Experience", sub: "Enterprise and regulated environments" },
  { stat: "Practitioner-Led", label: "Senior engineering judgment", sub: "Clear ownership · Evidence-ready" },
  { stat: "Regulated-Market Fluent", label: "Healthcare · Financial Services · SaaS · Logistics", sub: "Audit, customer, and insurer review experience" },
  { stat: "3-Phase", label: "Assess · Remediate · Validate", sub: "Find what is exposed. Fix what matters. Prove what is secure." },
];

const credibilityBlocks = [
  {
    label: "Practitioner Research & Threat Notes",
    text:
      "ArqeraAI publishes practitioner analysis on AI application security, agentic workflow risk, supply-chain exposure, prompt injection, and emerging vulnerabilities affecting AI-enabled systems. This research informs how we evaluate client environments and helps buyers assess our technical judgment before engaging.",
  },
];




export default function ArqProofBar() {
  return (
    <section className="bg-arq-bg py-20 md:py-[120px] px-6">
      <div className="max-w-[1200px] mx-auto">
        <div className="arq-label text-center mb-3">Why Us</div>
        <h2
          className="font-display font-semibold text-arq-text text-center mb-10 max-w-[820px] mx-auto"
          style={{ fontSize: "clamp(26px, 3.4vw, 36px)", lineHeight: 1.2 }}
        >
          We Connect Technical Risk to Business Value.
        </h2>

        {/* Outcome cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5 mb-14 max-w-[1100px] mx-auto">
          {[
            { title: "Exposure Reduced", body: "Findings prioritized by business impact, not just technical severity." },
            { title: "Controls Validated", body: "Evidence prepared for external and leadership review." },
            { title: "Deals Protected", body: "AI risk translated into customer trust, procurement confidence, and enterprise sales support." },
            { title: "Production AI Defended", body: "Agents, applications, vendors, and workflows reviewed against real operational exposure." },
          ].map((o) => (
            <div key={o.title} className="bg-arq-surface1 border border-arq-border rounded-xl p-5 md:p-6">
              <div className="font-display text-[16px] md:text-[17px] font-semibold text-arq-blue mb-2 leading-tight">
                {o.title}
              </div>
              <p className="text-arq-muted text-[13.5px] leading-[1.6]">{o.body}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-16">
          {stats.map((s) => (
            <div
              key={s.label}
              className="bg-arq-surface1 border border-arq-border rounded-xl p-6 md:p-7 text-center flex flex-col"
            >
              <div className="font-display font-bold text-[28px] md:text-[32px] text-arq-blue leading-none">
                {s.stat}
              </div>
              <div className="text-arq-text text-[14px] md:text-[15px] font-semibold mt-3 leading-snug">
                {s.label}
              </div>
              <div className="text-arq-muted text-[12px] mt-2 font-mono">{s.sub}</div>
            </div>
          ))}
        </div>

        {/* Sample finding cards */}
        <div className="mb-16">
          <div className="arq-label text-center mb-3">What a Finding Looks Like</div>
          <p className="text-arq-muted text-[14px] text-center max-w-[720px] mx-auto mb-6 leading-[1.65]">
            Every ArqeraAI finding is documented with severity, business exposure, control mapping, remediation status, and validation evidence.
          </p>
          <div className="grid md:grid-cols-2 gap-5 max-w-[1100px] mx-auto">
            {[
              {
                tag: "redacted_sample_01",
                severity: "Critical",
                severityClass: "text-arq-danger",
                dotColor: "bg-arq-danger",
                finding: "Indirect prompt injection path in vendor AI integration",
                context: "45 CFR 164.312(a)(1) · HIPAA Access Control",
                exposure: "Unscoped PHI access via downstream model context",
              },
              {
                tag: "redacted_sample_02",
                severity: "High",
                severityClass: "text-[#D97706]",
                dotColor: "bg-[#D97706]",
                finding: "Agent tool-use bypasses logging and approval workflow",
                context: "SOC 2 CC7.2 · NIST AI RMF MEASURE 2.7",
                exposure: "Privileged actions executed without audit trail or human-in-the-loop",
              },
            ].map((f) => (
              <div
                key={f.tag}
                className="bg-arq-bg/80 border border-arq-border rounded-xl overflow-hidden shadow-[0_8px_40px_rgba(0,0,0,0.4)]"
              >
                <div className="flex items-center justify-between px-4 py-3 bg-arq-surface1 border-b border-arq-border">
                  <div className="flex gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-arq-danger" />
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: "#D97706" }} />
                    <span className="w-2.5 h-2.5 rounded-full bg-arq-teal" />
                  </div>
                  <div className="font-mono text-[11px] text-arq-tertiary">ArqeraAI Assessment · {f.tag}</div>
                </div>
                <div className="p-5 md:p-6 font-mono text-[13px] md:text-[14px]">
                  <div className="grid grid-cols-[90px_1fr] md:grid-cols-[100px_1fr] gap-x-4 gap-y-3">
                    <span className="text-arq-tertiary">Severity</span>
                    <span className={`${f.severityClass} font-semibold`}>{f.severity}</span>

                    <span className="text-arq-tertiary">Finding</span>
                    <span className="text-arq-text">{f.finding}</span>

                    <span className="text-arq-tertiary">Context</span>
                    <span className="text-arq-text">{f.context}</span>

                    <span className="text-arq-tertiary">Exposure</span>
                    <span className="text-arq-text">{f.exposure}</span>

                    <span className="text-arq-tertiary">Status</span>
                    <span className="text-arq-teal font-semibold">Remediated · Validated</span>
                  </div>
                </div>
                <div className="px-5 py-3 border-t border-arq-border font-mono text-[11px] text-arq-tertiary">
                  ArqeraAI Assessment · production validation included
                </div>
              </div>
            ))}
          </div>
          <p className="text-arq-tertiary text-[12px] text-center mt-5 max-w-[640px] mx-auto leading-relaxed">
            Every finding documented, mapped to a control, remediated, and validated this way.
          </p>
        </div>




        {/* Framework authority strip */}
        <div className="mb-12">
          <div className="arq-label text-center mb-3">Frameworks We Work Against</div>
          <p className="text-arq-muted text-[14px] text-center max-w-[720px] mx-auto mb-6 leading-[1.65]">
            We map findings and evidence to the frameworks external reviewers expect, without
            making frameworks the center of the engagement.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 max-w-[920px] mx-auto">
            {[
              { mark: "OWASP", sub: "LLM Top 10 · 2025" },
              { mark: "MITRE", sub: "ATLAS v5.4" },
              { mark: "NIST", sub: "AI RMF 1.0" },
              { mark: "HIPAA", sub: "45 CFR 164" },
              { mark: "SOC 2", sub: "Trust Services Criteria" },
              { mark: "HITRUST", sub: "CSF v11" },
              { mark: "CMMC", sub: "Level 2 · 2.0" },
              { mark: "ISO", sub: "27001 · 42001" },
            ].map((f) => (
              <div
                key={f.mark}
                className="bg-arq-surface1 border border-arq-border rounded-lg px-4 py-5 text-center"
              >
                <div className="font-display font-bold text-arq-text text-[18px] md:text-[20px] tracking-[0.04em] leading-none">
                  {f.mark}
                </div>
                <div className="font-mono text-[10px] text-arq-tertiary mt-2 tracking-[0.05em]">
                  {f.sub}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Credibility blocks */}
        <div className="grid md:grid-cols-1 gap-6 max-w-[900px] mx-auto">
          {credibilityBlocks.map((b) => (
            <div
              key={b.label}
              className="bg-arq-surface1 border border-arq-border rounded-xl p-6 md:p-8"
            >
              <div className="font-mono-tech text-[11px] tracking-[0.12em] uppercase text-arq-blue mb-3">
                {b.label}
              </div>
              <p className="text-arq-text/90 text-[15px] leading-[1.7]">{b.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
